import { useState } from 'react';

const serviceOptions = [
  { id: 'bau-sanierungsdienste', label: 'Bau & Sanierungsdienste', icon: 'ri-hammer-line' },
  { id: 'sanierung', label: 'Sanierung & Renovierung', icon: 'ri-home-gear-line' },
  { id: 'trockenbau', label: 'Trockenbau', icon: 'ri-layout-masonry-line' },
  { id: 'fassade', label: 'Fassade & Außenwände', icon: 'ri-building-line' },
  { id: 'boden', label: 'Bodenbeläge', icon: 'ri-grid-line' },
  { id: 'maler', label: 'Malerarbeiten', icon: 'ri-brush-line' },
  { id: 'galabau', label: 'Garten- & Landschaftsbau', icon: 'ri-plant-line' },
  { id: 'zeichnungen', label: 'Bauzeichnungen', icon: 'ri-draft-line' },
  { id: 'kernsanierung', label: 'Kernsanierung', icon: 'ri-tools-line' },
  { id: 'energetisch', label: 'Energetische Sanierung', icon: 'ri-leaf-line' },
  { id: 'sonstiges', label: 'Sonstiges', icon: 'ri-more-line' },
];

const budgetOptions = [
  'Bis 5.000 €',
  '5.000 – 20.000 €',
  '20.000 – 50.000 €',
  '50.000 – 100.000 €',
  'Über 100.000 €',
  'Noch unklar',
];

const timelineOptions = [
  'So schnell wie möglich',
  'In den nächsten 1–3 Monaten',
  'In 3–6 Monaten',
  'In 6–12 Monaten',
  'Noch flexibel / Planung',
];

export default function KontaktForm() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);

  const toggleService = (id: string) => {
    setSelectedServices((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedServices.length === 0) return;
    setLoading(true);
    const form = e.currentTarget;
    const data = new FormData(form);
    const params = new URLSearchParams();
    data.forEach((value, key) => {
      if (key !== 'leistungen') params.append(key, value.toString());
    });
    const selectedLabels = serviceOptions
      .filter((s) => selectedServices.includes(s.id))
      .map((s) => s.label)
      .join(', ');
    params.append('leistungen', selectedLabels);

    try {
      const res = await fetch('https://readdy.ai/api/form/d72pc99sakbatd8b3oo0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        setError(false);
        form.reset();
        setCharCount(0);
        setSelectedServices([]);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-8 sm:p-10 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <i className="ri-checkbox-circle-fill text-3xl text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-3">Anfrage erfolgreich gesendet!</h3>
        <p className="text-gray-500 text-sm leading-relaxed max-w-sm mx-auto mb-6">
          Vielen Dank für Ihre Kontaktaufnahme. Wir melden uns innerhalb von 24 Stunden mit
          einem kostenlosen Festpreisangebot bei Ihnen.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="tel:+4921027398070"
            className="flex items-center justify-center gap-2 bg-[#0D2B4E] text-white px-6 py-3 rounded-lg font-semibold text-sm hover:bg-[#0a2240] transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line" /> Jetzt anrufen
          </a>
          <button
            onClick={() => setSubmitted(false)}
            className="px-6 py-3 rounded-lg border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Neue Anfrage stellen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8">
      <div className="mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">Kostenlos anfragen</h2>
        <p className="text-gray-400 text-sm">Alle Felder mit * sind Pflichtfelder.</p>
      </div>

      {error && (
        <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-error-warning-line text-red-500 text-lg" />
          </div>
          <p className="text-red-700 text-sm">
            Ein Fehler ist aufgetreten. Bitte erneut versuchen oder direkt anrufen.
          </p>
        </div>
      )}

      <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
        {/* Name */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Vorname *</label>
            <input
              type="text"
              name="vorname"
              required
              placeholder="Ihr Vorname"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-base sm:text-sm focus:outline-none focus:border-[#0D2B4E] transition-colors bg-gray-50 focus:bg-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Nachname *</label>
            <input
              type="text"
              name="nachname"
              required
              placeholder="Ihr Nachname"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-base sm:text-sm focus:outline-none focus:border-[#0D2B4E] transition-colors bg-gray-50 focus:bg-white"
            />
          </div>
        </div>

        {/* Email + Telefon */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">E-Mail *</label>
            <input
              type="email"
              name="email"
              required
              placeholder="ihre@email.de"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-base sm:text-sm focus:outline-none focus:border-[#0D2B4E] transition-colors bg-gray-50 focus:bg-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Telefon</label>
            <input
              type="tel"
              name="telefon"
              placeholder="Ihre Telefonnummer"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-base sm:text-sm focus:outline-none focus:border-[#0D2B4E] transition-colors bg-gray-50 focus:bg-white"
            />
          </div>
        </div>

        {/* Leistungsauswahl Multi-Select */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Gewünschte Leistung(en) *{' '}
            <span className="text-gray-400 font-normal">(Mehrfachauswahl möglich)</span>
          </label>
          {selectedServices.length === 0 && (
            <p className="text-xs text-red-400 mb-2">Bitte mindestens eine Leistung auswählen.</p>
          )}
          <div className="grid grid-cols-1 min-[480px]:grid-cols-2 gap-2">
            {serviceOptions.map((s) => {
              const active = selectedServices.includes(s.id);
              return (
                <button
                  type="button"
                  key={s.id}
                  onClick={() => toggleService(s.id)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-left transition-all text-sm cursor-pointer ${
                    active
                      ? 'border-[#0D2B4E] bg-[#0D2B4E]/5 text-[#0D2B4E] font-medium'
                      : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className={`${s.icon} text-sm`} />
                  </div>
                  <span className="leading-tight">{s.label}</span>
                  {active && (
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 ml-auto">
                      <i className="ri-check-line text-[#0D2B4E] text-sm font-bold" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Budget + Timeline */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Projektbudget</label>
            <select
              name="budget"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-base sm:text-sm focus:outline-none focus:border-[#0D2B4E] transition-colors bg-gray-50 focus:bg-white"
            >
              <option value="">Bitte auswählen</option>
              {budgetOptions.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Gewünschter Start</label>
            <select
              name="zeitplan"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-base sm:text-sm focus:outline-none focus:border-[#0D2B4E] transition-colors bg-gray-50 focus:bg-white"
            >
              <option value="">Bitte auswählen</option>
              {timelineOptions.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Projektstandort */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Projektstandort / PLZ</label>
          <input
            type="text"
            name="standort"
            placeholder="z.B. Ratingen, 40880"
            className="w-full px-4 py-3 border border-gray-200 rounded-lg text-base sm:text-sm focus:outline-none focus:border-[#0D2B4E] transition-colors bg-gray-50 focus:bg-white"
          />
        </div>

        {/* Nachricht */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Projektbeschreibung *
            <span className={`ml-2 font-normal text-xs ${charCount >= 450 ? 'text-red-400' : 'text-gray-400'}`}>
              ({charCount}/500)
            </span>
          </label>
          <textarea
            name="nachricht"
            required
            rows={4}
            maxLength={500}
            placeholder="Beschreiben Sie Ihr Bauvorhaben, die gewünschten Arbeiten, besondere Anforderungen und ggf. Ihren Zeitplan..."
            onChange={(e) => setCharCount(e.target.value.length)}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg text-base sm:text-sm focus:outline-none focus:border-[#0D2B4E] transition-colors bg-gray-50 focus:bg-white resize-none"
          />
        </div>

        {/* Kontaktweg */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Bevorzugter Kontaktweg</label>
          <div className="flex flex-wrap gap-2">
            {['E-Mail', 'Telefon', 'WhatsApp', 'Egal'].map((k) => (
              <label key={k} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-full text-sm cursor-pointer hover:border-[#0D2B4E]/40 transition-colors">
                <input type="radio" name="kontaktweg" value={k} className="accent-[#0D2B4E]" />
                <span>{k}</span>
              </label>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || selectedServices.length === 0}
          className="w-full bg-[#0D2B4E] text-white py-4 rounded-lg font-bold text-base hover:bg-[#0a2240] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <i className="ri-loader-4-line animate-spin" />
              <span>Wird gesendet...</span>
            </>
          ) : (
            <>
              <i className="ri-send-plane-fill" />
              <span>Kostenloses Angebot anfordern</span>
            </>
          )}
        </button>

        <p className="text-xs text-gray-400 text-center">
          Mit dem Absenden stimmen Sie unserer{' '}
          <a href="/datenschutz" className="underline hover:text-gray-600">Datenschutzerklärung</a> zu.
        </p>
      </form>
    </div>
  );
}
